import { defineStore } from 'pinia'
import { ref } from 'vue'
import client from '../api/client.js'
import { useKeycloakStore } from './keycloak.js'

export const useMembersStore = defineStore('members', () => {
  const members = ref([])
  const sentInvitations = ref([])
  const joinRequests = ref([])
  const loading = ref(false)

  async function fetchMembers(groupId) {
    loading.value = true
    try {
      const { data } = await client.get(`groups/${groupId}/members`)
      members.value = data
      await fetchEngagement(groupId, data)
      return members.value
    } finally {
      loading.value = false
    }
  }

  /**
   * Attach engagement scores to the loaded members.
   *
   * The endpoint exposes per-member contribution totals and is therefore
   * restricted to active group members server-side, so we only call it when the
   * signed-in user actually appears in the member list. Visitors browsing a
   * public group skip the request entirely instead of provoking a 403.
   */
  async function fetchEngagement(groupId, memberList) {
    const currentUserId = useKeycloakStore().subject
    if (!currentUserId) return
    const isMember = memberList.some(
      m => m.userId != null && String(m.userId) === String(currentUserId))
    if (!isMember) return
    try {
      const { data: engagement } = await client.get(
        `groups/${groupId}/members/engagement`)
      const byId = Object.fromEntries(engagement.map(e => [e.userId, e.score]))
      members.value = members.value.map(m => ({
        ...m, engagementScore: byId[m.userId] ?? null,
      }))
    } catch {
      // engagement unavailable — cards render without tier badges
    }
  }

  async function fetchInvitations(groupId) {
    const { data } = await client.get(`groups/${groupId}/invitations`)
    sentInvitations.value = data.filter(i => i.status === 'PENDING')
    joinRequests.value = data.filter(i => i.status === 'REQUESTED')
  }

  async function changeRole(groupId, userId, role) {
    await client.patch(`groups/${groupId}/members/${userId}`, { role })
    const m = members.value.find(m => m.userId === userId)
    if (m) m.role = role
  }

  async function removeMember(groupId, userId) {
    await client.delete(`groups/${groupId}/members/${userId}`)
    members.value = members.value.filter(m => m.userId !== userId)
  }

  async function nudge(groupId, userId) {
    await client.post(`groups/${groupId}/members/${userId}/nudge`)
  }

  async function inviteMember(groupId, payload) {
    const { data } = await client.post(`groups/${groupId}/invite`, payload)
    sentInvitations.value.unshift(data)
    return data
  }

  async function resendInvitation(groupId, invitationId) {
    await client.post(`groups/${groupId}/invitations/${invitationId}/resend`)
  }

  async function acceptJoinRequest(groupId, invitationId) {
    await client.post(`groups/${groupId}/invitations/${invitationId}/accept`)
    joinRequests.value = joinRequests.value.filter(i => i.id !== invitationId)
    await fetchMembers(groupId)
  }

  async function declineJoinRequest(groupId, invitationId) {
    await client.post(`groups/${groupId}/invitations/${invitationId}/decline`)
    joinRequests.value = joinRequests.value.filter(i => i.id !== invitationId)
  }

  return {
    members, sentInvitations, joinRequests, loading,
    fetchMembers, fetchInvitations, changeRole, removeMember,
    nudge, inviteMember, resendInvitation, acceptJoinRequest, declineJoinRequest,
  }
})
